import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Pipette, Plus, X } from 'lucide-react';
import { useTools } from '../../ToolProvider.jsx';
import { useEditorTypeRightContext, useEditorTypeRightToggleContext } from '../../SelectedTypeProvider.jsx';
import {
    forgetCustomColor,
    hasScreenEyeDropper,
    normalizeCustomColor,
    pickCanvasColor,
    pickScreenColor,
    rememberCustomColor,
    useCustomColors,
} from '../../customColors.js';
import HsvColorPicker from './HsvColorPicker.jsx';

/**
 * Color libre y cuentagotas para cualquier paleta (C3D-17).
 *
 * `onPick(hex)` recibe el color ya normalizado; además se guarda en "Tus
 * colores", que se pintan aquí mismo para volver a usarlos de un clic.
 *
 * - `variant="panel"` (panel derecho): el ＋ despliega aquí mismo el selector.
 * - `variant="strip"` (carrusel del lienzo): no hay sitio para el selector, así
 *   que el ＋ abre el panel de color de la derecha con él ya desplegado.
 *
 * `canvasPick` permite el cuentagotas sobre el lienzo 2D cuando el navegador no
 * trae el suyo; fuera del 2D (paletas del 3D) el lienzo no se ve y no aplica.
 */
const CustomColorControls = ({ onPick, activeColor = null, variant = 'panel', canvasPick = true }) => {
    const { canvas: editorCanvas } = useTools() || {};
    const canvas = canvasPick ? editorCanvas : null;
    const editorTypeRight = useEditorTypeRightContext();
    const setEditorTypeRight = useEditorTypeRightToggleContext();
    const customColors = useCustomColors();
    const strip = variant === 'strip';
    const [pickerOpen, setPickerOpen] = useState(
        () => !strip && Boolean(editorTypeRight?.data?.customColor),
    );
    const [picking, setPicking] = useState(false);
    // Modo para quitar colores de "Tus colores" (solo en el panel).
    const [editing, setEditing] = useState(false);

    // El ＋ del carrusel con el panel de color ya abierto: no se vuelve a montar,
    // así que el selector se despliega al llegar la petición.
    useEffect(() => {
        if (!strip && editorTypeRight?.data?.customColor) setPickerOpen(true);
    }, [strip, editorTypeRight?.data]);

    // Quitado el último, no queda nada que editar.
    useEffect(() => {
        if (!customColors.length) setEditing(false);
    }, [customColors.length]);

    const canEyeDrop = hasScreenEyeDropper() || Boolean(canvas);

    const apply = (value) => {
        const hex = normalizeCustomColor(value);
        if (!hex) return;
        rememberCustomColor(hex);
        onPick(hex);
    };

    const handleEyeDropper = async () => {
        setPicking(true);
        try {
            apply(hasScreenEyeDropper() ? await pickScreenColor() : await pickCanvasColor(canvas));
        } finally {
            setPicking(false);
        }
    };

    const handleNewColor = () => {
        if (strip) {
            setEditorTypeRight?.({ type: 'editor_color', data: { customColor: true } });
            return;
        }
        setPickerOpen(open => !open);
    };

    const isActive = (color) => activeColor?.toLowerCase() === color;
    const swatchClass = strip
        ? 'customiz3r-color-carousel__swatch'
        : 'h-[30px] w-[30px] m-[5px] cursor-pointer rounded-sm';
    const buttonClass = strip
        ? 'btn btn-xs btn-circle btn-ghost shrink-0'
        : 'btn btn-sm btn-square m-[5px] border border-base-300';

    const controls = (
        <>
            {customColors.map(color => (
                <button
                    key={color}
                    type="button"
                    className={`${swatchClass} relative ${strip && isActive(color) ? 'is-active' : ''}`}
                    // Para la lupa del carrusel, que busca el color bajo el dedo por esta marca.
                    data-custom-color={strip ? color : undefined}
                    style={{
                        backgroundColor: color,
                        ...(!strip && isActive(color) ? { border: '2px solid black' } : {}),
                    }}
                    title={editing ? `Quitar ${color}` : `Tu color ${color}`}
                    aria-label={editing ? `Quitar ${color} de tus colores` : `Tu color ${color}`}
                    onClick={() => (editing ? forgetCustomColor(color) : apply(color))}
                >
                    {editing && (
                        <span className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-error text-error-content shadow">
                            <X className="h-3 w-3" />
                        </span>
                    )}
                </button>
            ))}

            <button
                type="button"
                className={`${buttonClass} ${pickerOpen ? 'btn-primary' : strip ? '' : 'btn-ghost'}`}
                title="Nuevo color"
                aria-label="Nuevo color"
                aria-expanded={strip ? undefined : pickerOpen}
                onClick={handleNewColor}
            >
                <Plus className="h-4 w-4" />
            </button>

            {canEyeDrop && (
                <button
                    type="button"
                    className={`${buttonClass} ${picking ? 'btn-active' : strip ? '' : 'btn-ghost'}`}
                    title={hasScreenEyeDropper()
                        ? 'Cuentagotas: coger un color de la pantalla'
                        : 'Cuentagotas: pincha en un objeto del lienzo (Esc cancela)'}
                    aria-label="Cuentagotas"
                    disabled={picking}
                    onClick={handleEyeDropper}
                >
                    <Pipette className="h-4 w-4" />
                </button>
            )}
        </>
    );

    if (strip) return controls;

    return (
        <div className="mt-3">
            <div className="mb-1 flex items-center justify-between">
                <span className="text-xs font-medium text-base-content/60">
                    {customColors.length ? 'Tus colores' : 'Otro color'}
                </span>
                {(customColors.length > 0 || editing) && (
                    <button
                        type="button"
                        className="btn btn-ghost btn-xs"
                        aria-pressed={editing}
                        onClick={() => setEditing(value => !value)}
                    >
                        {editing ? 'Hecho' : 'Editar'}
                    </button>
                )}
            </div>
            {editing && (
                <p className="mb-1 text-xs text-base-content/60">
                    Pulsa un color para quitarlo de tus colores. Lo que ya está pintado no cambia.
                </p>
            )}
            <div className="flex flex-wrap items-center">{controls}</div>
            {picking && !hasScreenEyeDropper() && (
                <p className="mt-1 text-xs text-base-content/60">Pincha en un objeto del lienzo para coger su color.</p>
            )}
            {pickerOpen && (
                <div className="mt-2 rounded-lg border border-base-300 bg-base-200 p-3">
                    <HsvColorPicker value={normalizeCustomColor(activeColor) || '#ff0000'} onCommit={apply} />
                </div>
            )}
        </div>
    );
};

CustomColorControls.propTypes = {
    onPick: PropTypes.func.isRequired,
    activeColor: PropTypes.string,
    variant: PropTypes.oneOf(['panel', 'strip']),
    canvasPick: PropTypes.bool,
};

export default CustomColorControls;
